/**
 * Réordonner une liste en la faisant glisser, au doigt comme à la souris.
 *
 * Les événements de glissement natifs du navigateur (`dragstart` et compagnie) ne se déclenchent
 * pas au toucher : sur un téléphone — ce que cette application est d'abord —, ils ne font rien.
 * On passe donc par les événements de pointeur, qui couvrent le doigt, le stylet et la souris du
 * même geste.
 *
 * Le calcul lui-même est ici, séparé du composant : « quel rang occupe un point à telle hauteur »
 * est une question de géométrie, qui s'éprouve sans navigateur.
 */

/** Le milieu vertical de chaque ligne, dans l'ordre où elles sont affichées. */
export type Milieux = readonly number[]

/**
 * Le rang qu'occuperait l'élément relâché à cette hauteur.
 *
 * Comparé au <b>milieu</b> de chaque ligne et non à son bord : une ligne ne cède sa place qu'une
 * fois dépassée de moitié, ce qui évite qu'un tremblement de doigt sur une frontière fasse
 * clignoter deux rangs.
 *
 * @param milieux milieux des lignes, sans celle qu'on déplace.
 * @param hauteur position verticale du pointeur, dans le même repère.
 */
export function rangPourHauteur(milieux: Milieux, hauteur: number): number {
  let rang = 0

  while (rang < milieux.length && hauteur > milieux[rang]!) {
    rang += 1
  }

  return rang
}

/**
 * Déplace un élément d'un rang à un autre.
 *
 * Rend une nouvelle liste : l'ancienne reste disponible pour revenir en arrière si
 * l'enregistrement échoue.
 */
export function deplacer<T>(liste: readonly T[], depuis: number, vers: number): T[] {
  const copie = [...liste]
  const [element] = copie.splice(depuis, 1)

  if (element === undefined) {
    return copie
  }

  copie.splice(Math.max(0, Math.min(vers, copie.length)), 0, element)

  return copie
}

/** La position d'une ligne au moment où le glissement commence. */
export type Empreinte = { readonly haut: number; readonly hauteur: number }

/**
 * Le rang visé par un élément qu'on déplace de `dy` pixels.
 *
 * Calculé sur une <b>empreinte</b> prise au départ du geste, et non sur les positions
 * courantes : si la liste se réorganise sous le doigt, les positions changent à chaque image et
 * la cible se met à osciller entre deux rangs. L'empreinte, elle, ne bouge pas.
 *
 * @param empreintes positions de toutes les lignes au départ, dans l'ordre affiché.
 * @param depuis rang de la ligne déplacée.
 * @param dy déplacement vertical du doigt depuis le départ.
 */
export function cibleDuDeplacement(
  empreintes: readonly Empreinte[],
  depuis: number,
  dy: number,
): number {
  const tenue = empreintes[depuis]

  if (!tenue) {
    return depuis
  }

  const centre = tenue.haut + tenue.hauteur / 2 + dy
  let cible = depuis

  // Vers le bas : on dépasse une ligne dès que son centre est franchi.
  for (let rang = depuis + 1; rang < empreintes.length; rang += 1) {
    const autre = empreintes[rang]!

    if (centre > autre.haut + autre.hauteur / 2) {
      cible = rang
    }
  }

  // Vers le haut, symétriquement.
  for (let rang = depuis - 1; rang >= 0; rang -= 1) {
    const autre = empreintes[rang]!

    if (centre < autre.haut + autre.hauteur / 2) {
      cible = rang
    }
  }

  return cible
}

/**
 * De combien décaler une ligne qui n'est pas celle qu'on tient, pour laisser la place.
 *
 * C'est ce décalage qui fait voir où l'élément va tomber : sans lui, on déplace un bloc dans le
 * vide et rien ne dit ce qui se passera au relâchement.
 */
export function decalageVoisin(
  rang: number,
  depuis: number,
  cible: number,
  hauteurTenue: number,
): number {
  if (rang === depuis) {
    return 0
  }

  if (depuis < cible && rang > depuis && rang <= cible) {
    return -hauteurTenue
  }

  if (depuis > cible && rang < depuis && rang >= cible) {
    return hauteurTenue
  }

  return 0
}
